// @ts-check
/// <reference path="../types/index.js" />

import React, { createContext, useState, useCallback } from 'react';

/** @type {ResetWindowDataContextType} */
const defaultContextValue = {
  triggerAllResets: () => {},
  triggerResetByLabel: () => {},
  registerResetFunction: () => {},
  unregisterResetFunction: () => {},
};

const ResetWindowDataContext = createContext(defaultContextValue);

/**
 * Provides the ResetWindowDataProvider to child components.
 * @param {{ children: React.ReactNode }} props - The props containing children to be rendered within the provider.
 * @return {JSX.Element} The provider component.
 */
export const ResetWindowDataProvider = ({ children }) => {
  const [resetFunctions, setResetFunctions] = useState({});

  const registerResetFunction = useCallback((label, fn) => {
    setResetFunctions((prev) => ({ ...prev, [label]: fn }));
  }, []);

  const unregisterResetFunction = useCallback((label) => {
    setResetFunctions((prev) => {
      const newState = { ...prev };
      delete newState[label];
      return newState;
    });
  }, []);

  const triggerAllResets = useCallback(() => {
    Object.values(resetFunctions).forEach((fn) => fn());
  }, [resetFunctions]);

  const triggerResetByLabel = useCallback(
    (label) => {
      if (resetFunctions[label]) {
        resetFunctions[label]();
      }
    },
    [resetFunctions],
  );

  return (
    <ResetWindowDataContext.Provider
      value={{
        triggerAllResets,
        triggerResetByLabel,
        registerResetFunction,
        unregisterResetFunction,
      }}
    >
      {children}
    </ResetWindowDataContext.Provider>
  );
};

export default ResetWindowDataContext;
